import React, { useEffect, useState } from 'react';
import './CICPLetter.page.css';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { UserUtils } from '../../utils/UserUtils';
import UserTokenData from '../../types/UserTokenData';
import { LoginState } from '../../types/LoginState';

interface CICPLetterProps {
  // If provided as props, the component won't fetch data from API
  userId?: string;
  date?: string;
  clientFirstName?: string;
  clientLastName?: string;
  attorneyName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  // If true, skips authentication and routing logic (for embedded use)
  asComponent?: boolean;
}

interface CICPLetterData {
  date: string;
  clientFirstName: string;
  clientLastName: string;
  attorneyName: string;
  emailAddress: string;
  phoneNumber: string;
}

interface PatientDetailObject {
  status: string;
  email: string;
  firstName: string;
  entityId: string;
  lastName: string;
  patientAssignee: {
    Lawyer: Array<{
      firstName: string;
      lastName: string;
      email: string;
      userEntityAccessId: string;
    }>;
    Physician: Array<{
      firstName: string;
      lastName: string;
      userEntityAccessId: string;
    }>;
  };
}

function CICPLetterPage(props?: CICPLetterProps) {
  const asComponent = props?.asComponent || false;

  const [token, setToken] = useState<string>(
    localStorage.getItem('token') || ''
  );
  const { userId: routeUserId } = useParams();
  const userId = props?.userId || routeUserId;
  const nav = useNavigate();

  // Initialize with props if provided, otherwise use defaults
  const [letterData, setLetterData] = useState<CICPLetterData>({
    date: props?.date || '[Date]',
    clientFirstName: props?.clientFirstName || '[client first and last name]',
    clientLastName: props?.clientLastName || '',
    attorneyName: props?.attorneyName || '[ATTORNEY NAME]',
    emailAddress: props?.emailAddress || '[EMAIL ADDRESS]',
    phoneNumber: props?.phoneNumber || '[PHONE NUMBER]',
  });

  const [userDetails, setUserDetails] = useState<UserTokenData>({
    userName: '',
    id: '',
    email: '',
    role: '',
  });

  const [patientDetails, setPatientDetails] = useState<PatientDetailObject>({
    status: '',
    email: '',
    firstName: '',
    entityId: '',
    lastName: '',
    patientAssignee: {
      Lawyer: [],
      Physician: [],
    },
  });

  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });

  useEffect(() => {
    // Skip authentication when used as a component
    if (asComponent) return;

    if (token == '') {
      nav('/signin');
    }
    const userDetails = UserUtils.decodeUserToken(token);
    if (!userDetails) {
      nav('/signin');
    } else {
      setUserDetails(userDetails);
      console.log('userDetails', userDetails);
    }
  }, [token, asComponent]);

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });
      if (!response.ok) {
        setLoginState({ isLoggedIn: false, isAdmin: false });
        nav('/');
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        setLoginState({ isLoggedIn: true, isAdmin: false });
      }
    }
  };

  const getUserDetails = async () => {
    const url = `/admin/caseDetails/${userId}?detailed=true`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`, // Include the token in the Authorization header
      },
    });
    const data = await response.json();
    console.log('patientDetails', data);
    setPatientDetails(data);
  };

  useEffect(() => {
    // Skip fetching when used as a component with props
    if (asComponent) return;

    if (userDetails.id) {
      validateUserLogin();
      getUserDetails();
    }
  }, [userDetails.id, asComponent]);

  // Update letter data when patient details are fetched (only when not using props)
  useEffect(() => {
    // Skip if data provided via props
    if (asComponent && props) return;

    if (patientDetails.firstName) {
      const lawyer = patientDetails.patientAssignee.Lawyer[0];
      const today = new Date();
      const formattedDate = today.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      setLetterData({
        date: formattedDate,
        clientFirstName: patientDetails.firstName,
        clientLastName: patientDetails.lastName,
        attorneyName: lawyer
          ? `${lawyer.firstName} ${lawyer.lastName}`
          : '[ATTORNEY NAME]',
        emailAddress: lawyer ? lawyer.email : '[EMAIL ADDRESS]',
        phoneNumber: '[PHONE NUMBER]', // TODO: Add phone number to lawyer data if needed
      });
    }
  }, [patientDetails, asComponent, props]);

  const clientFullName = letterData.clientLastName
    ? `${letterData.clientFirstName} ${letterData.clientLastName}`
    : letterData.clientFirstName;

  return (
    <div className="CICPLetterContainer">
      <div className="CICPLetterContent">
        {/* Header Section */}
        <div className="CICPLetterHeader">
          <p className="CICPLetterDate">{letterData.date}</p>
        </div>

        {/* Recipient Address */}
        <div className="CICPLetterRecipient">
          <p>The Honorable Robert F. Kennedy Jr.</p>
          <p>Secretary</p>
          <p>U.S. Department of Health and Human Services</p>
          <p>200 Independence Avenue S.W.</p>
          <p>Washington, D.C. 20201</p>
          <p>Rfk54@hhs.gov</p>
        </div>

        {/* Subject Line */}
        <div className="CICPLetterSubject">
          <p>
            <span style={{ fontStyle: 'italic' }}>
              Re: Request for Reconsideration of{' '}
            </span>
            <span style={{ fontStyle: 'italic' }}>{clientFullName}</span>
            <span style={{ fontStyle: 'italic' }}>
              's CICP Request for Benefits Determination
            </span>
          </p>
        </div>

        {/* Salutation */}
        <div className="CICPLetterSalutation">
          <p>Dear Secretary Kennedy:</p>
        </div>

        {/* Body Paragraphs */}
        <div className="CICPLetterBody">
          <p className="CICPLetterParagraph">
            On behalf of <strong>{clientFullName}</strong>, we respectfully
            submit the following in support of a request for reconsideration of
            a determination under the Countermeasures Injury Compensation
            Program ("CICP").
          </p>

          <p className="CICPLetterParagraph">
            Pursuant to your authority under 42 CFR § 110.9142 and C.F.R. §
            110.74(c), we ask that you re-evaluate and grant this previously
            denied request for benefits in your capacity as Secretary of Health
            and Human Services. This letter includes all required information
            and supporting documentation, including the original request for
            benefits, a written narrative, scientific materials supporting the
            claimed COVID-19 vaccine injury, and financial information
            supporting an award.
          </p>

          <p className="CICPLetterParagraph">
            We appreciate your consideration of this matter and look forward to
            your review. We welcome any questions and can be reached via the
            email address or phone number below.
          </p>
        </div>

        {/* Closing */}
        <div className="CICPLetterClosing">
          <p>Very truly yours,</p>
        </div>

        {/* Signature Block */}
        <div className="CICPLetterSignature">
          <div className="CICPLetterSignatureLine"></div>
          <p className="CICPLetterSignatureField">{letterData.attorneyName}</p>
          <p className="CICPLetterSignatureField">{letterData.emailAddress}</p>
          <p className="CICPLetterSignatureField">(508) 369-6870</p>
        </div>
      </div>
    </div>
  );
}

export default CICPLetterPage;
